import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import api from '../../lib/api';
import { Search, ChevronDown, User, LogOut, LayoutDashboard, Menu, X, ShieldCheck, ShoppingCart } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../ui/dropdown-menu';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/settings/public').then(r => setSiteSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(searchQuery)}`).then(r => {
        setSearchResults(r.data);
        setShowSearch(true);
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    function handleClick(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = async () => { await logout(); navigate('/'); };
  const isAdmin = user && (user.role === 'admin' || user.role === 'root_admin');

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0 min-h-[36px]" data-testid="logo-link">
          {siteSettings === null ? (
            <div className="h-9 w-[120px]" />
          ) : siteSettings?.header_logo_url ? (
            <img src={siteSettings.header_logo_url} alt={siteSettings.site_name || 'Astha Abhyas'} className="h-9 w-auto max-w-[160px] object-contain" />
          ) : (
            <>
              <div className="w-9 h-9 rounded-lg bg-[#7551e8] flex items-center justify-center">
                <span className="text-white font-bold text-lg font-['Outfit']">A</span>
              </div>
              <span className="font-['Outfit'] font-semibold text-xl text-[#0A0A0A] hidden sm:block">{siteSettings?.site_name || 'Astha Abhyas'}</span>
            </>
          )}
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6 ml-8">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 text-sm font-medium text-[#52525B] hover:text-[#7551e8] transition-colors" data-testid="categories-dropdown">
                Exams <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {categories.map(cat => (
                <DropdownMenuItem key={cat.id} onClick={() => navigate(`/?category=${cat.id}`)} data-testid={`cat-${cat.name}`}>
                  {cat.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/" className="text-sm font-medium text-[#52525B] hover:text-[#7551e8] transition-colors">Home</Link>
        </nav>

        {/* Search */}
        <div className="flex-1 max-w-md mx-4 relative" ref={searchRef}>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search test series..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowSearch(true)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-zinc-200 rounded-lg bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#7551e8]/30 focus:border-[#7551e8] transition-all"
              data-testid="search-input"
            />
          </div>
          {showSearch && searchResults.length > 0 && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
              {searchResults.map(s => (
                <button key={s.id} onClick={() => { navigate(`/test-series/${s.id}`); setShowSearch(false); setSearchQuery(''); }}
                  className="w-full text-left px-4 py-3 hover:bg-[#F4F0FF] text-sm border-b border-zinc-100 last:border-0 transition-colors"
                  data-testid={`search-result-${s.id}`}>
                  <span className="font-medium text-[#0A0A0A]">{s.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Auth */}
        <div className="hidden lg:flex items-center gap-3">
          <button onClick={() => navigate('/cart')} className="relative p-2 rounded-lg hover:bg-zinc-100 transition-colors" data-testid="cart-icon-btn">
            <ShoppingCart size={20} className="text-[#52525B]" />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#7551e8] text-white text-xs font-bold rounded-full flex items-center justify-center">
                {itemCount}
              </span>
            )}
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-zinc-100 transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-[#7551e8] flex items-center justify-center">
                    <span className="text-white text-sm font-medium">{(user.name || user.email)?.[0]?.toUpperCase()}</span>
                  </div>
                  <span className="text-sm font-medium text-[#0A0A0A] max-w-[100px] truncate">{user.name || user.email}</span>
                  <ChevronDown size={14} className="text-zinc-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">
                  <LayoutDashboard size={14} className="mr-2" /> Dashboard
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin">
                    <ShieldCheck size={14} className="mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                  <LogOut size={14} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-sm" data-testid="nav-login-btn">Log In</Button>
              <Button onClick={() => navigate('/register')} className="text-sm bg-[#7551e8] hover:bg-[#5e3dc7] text-white" data-testid="nav-register-btn">Sign Up</Button>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-zinc-200 bg-white px-4 py-4 space-y-3">
          <Link to="/" className="block py-2 text-sm font-medium" onClick={() => setMobileOpen(false)}>Home</Link>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => { navigate(`/?category=${cat.id}`); setMobileOpen(false); }}
              className="block py-2 text-sm text-[#52525B] w-full text-left">{cat.name}</button>
          ))}
          <div className="border-t border-zinc-200 pt-3 space-y-2">
            {user ? (
              <>
                <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}>Dashboard</Button>
                {isAdmin && <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/admin'); setMobileOpen(false); }}>Admin</Button>}
                <Button variant="ghost" className="w-full justify-start text-red-500" onClick={() => { handleLogout(); setMobileOpen(false); }}>Logout</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" className="w-full" onClick={() => { navigate('/login'); setMobileOpen(false); }}>Log In</Button>
                <Button className="w-full bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={() => { navigate('/register'); setMobileOpen(false); }}>Sign Up</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
